import React, { createContext, useReducer, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LoadingOverlay,
  Box,
  Text,
  Stack,
  ThemeIcon,
  Center,
  Group,
  Loader,
  Button,
  MantineProvider,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconFaceIdError,
  IconExclamationCircle,
} from "@tabler/icons-react";
import ErrorMessage from "../components/error-message";
import Navigation from "../components/navigation";
import CenterLoadingOverlay from "../components/center-loading-overlay";
import EnglishLoading from "../components/english-loading";
import EnglishLoadingWithCompletion from "../components/english-loading-with-completion";
import {
  useVisitorData,
  FingerprintJSPro,
} from "@fingerprintjs/fingerprintjs-pro-react";
// Creating the context
export const DataContext = createContext();

// Initial state for the reducer
const initialState = {
  data: null,
  settings: null,
  text: null,
  routeSequence: [],
  sessionState: {},
  currentIndex: 0,
  alreadyDone: false,
  triggerNavigation: false,
  status: "PENDING",
  totalRoutes: 0,
  completedRoutes: 0,
  selectedComponent: null,
  modularFailedAtLeastOne: false,
  referrer: "",
  closedClicked: false,
  confirmationNumber: null, // Add this to track the latest confirmation number
  // Add other states as necessary
};

// Reducer function to handle state changes
const dataReducer = (state, action) => {
  switch (action.type) {
    case "SET_DATA":
      return { ...state, data: action.payload };
    case "SET_STATUS":
      return { ...state, status: action.payload };
    case "SET_SELECTED_COMPONENT":
      return { ...state, selectedComponent: action.payload };
    case "SET_CLOSED_CLICKED":
      return { ...state, closedClicked: action.payload };
    case "SET_MODULAR_FAILED_AT_LEAST_ONE":
      return { ...state, modularFailedAtLeastOne: action.payload };
    case "TRIGGER_NAVIGATION":
      return { ...state, triggerNavigation: !state.triggerNavigation }; // Toggle the trigger
    case "SET_IS_CURRENT_COMPONENT_COMPLETE":
      return { ...state, isCurrentComponentComplete: action.payload };
    case "SET_SETTINGS":
      return { ...state, settings: action.payload };
    case "SET_TEXT":
      return { ...state, text: action.payload };
    case "SET_SESSION_STATE":
      return { ...state, sessionState: action.payload };
    case "SET_CURRENT_INDEX":
      return { ...state, currentIndex: action.payload };
    case "SET_ALREADY_DONE":
      return { ...state, alreadyDone: action.payload };
    case "SET_REFERRER":
      return { ...state, referrer: action.payload };
    case "SET_CONFIRMATION_NUMBER":
      return { ...state, confirmationNumber: action.payload };
    case "SET_ROUTE_SEQUENCE":
      return {
        ...state,
        routeSequence: action.payload,
        totalRoutes: action.payload.length, // Set the total number of routes
      };
    case "INCREMENT_COMPLETED_ROUTES":
      return {
        ...state,
        completedRoutes: state.completedRoutes + 1,
      };
    // Add more cases as needed
    default:
      return state;
  }
};

async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

// DataProvider component
export const DataProvider = ({ children, request_id, fingerprintDisabled = false }) => {
  // Create a unique instance ID to track if component is remounting
  const instanceId = React.useRef(Math.random().toString(36).substr(2, 9));
  console.log('🏗️ DataProvider instance created/rendered:', {
    instanceId: instanceId.current,
    request_id,
    timestamp: new Date().toISOString()
  });
  
  // Always start with default state to prevent remounting
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qr = searchParams.get("qr");
  const nextPath = searchParams.get("nextPath");
  const edge = searchParams.get("edge");
  const language = searchParams.get("language");
  
  // Check if English loading should be shown
  // Use URL parameter if available, otherwise fall back to browser language
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const effectiveLanguage = language || browserLanguage;
    // Check if URL contains specific words that should skip English loading
  const urlParams = new URLSearchParams(window.location.search);
  const currentUrl = window.location.href.toLowerCase();
  const skipEnglishLoadingWords = ['handoff', 'oneid', 'clear'];
  const shouldSkipEnglishLoading = skipEnglishLoadingWords.some(word => 
    currentUrl.includes(word) || urlParams.has(word)
  );
  
  const shouldShowEnglishLoading = !shouldSkipEnglishLoading && (!effectiveLanguage || effectiveLanguage.toLowerCase() === "" || effectiveLanguage.toLowerCase().startsWith("en"));
  
  // Only require animation completion for English users
  const [loadingAnimationComplete, setLoadingAnimationComplete] = useState(!shouldShowEnglishLoading);
  // Add delay before showing English loading to prevent restart
  const [showEnglishLoadingDelayed, setShowEnglishLoadingDelayed] = useState(false);
  const [fpjs, setFpjs] = useState(fingerprintDisabled ? { visitorId: 'disabled-via-url-param' } : null);
  const [fingerprintSent, setFingerprintSent] = useState(fingerprintDisabled);
  const [errorGettingData, setErrorGettingData] = useState(false);
  const [edgeRetryCount, setEdgeRetryCount] = useState(0);
  const [showHighTrafficMessage, setShowHighTrafficMessage] = useState(false);
  const [overload, setOverload] = useState(false);
  const [isBot, setIsABot] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // Track number of retries

  // Store the fingerprint disabled state from props
  const [internalFingerprintDisabled, setInternalFingerprintDisabled] = useState(fingerprintDisabled);

  const postMessage = (message) => {
    if (state?.settings?.isReactNative && window.ReactNativeWebView?.postMessage) {
      const data =
        typeof message === "string" ? message : JSON.stringify(message);
      window.ReactNativeWebView.postMessage(data);
    } else {
      window.parent.postMessage(message, "*");
    }
  };

  const { getData } = useVisitorData(
    { extendedResult: false, tag: { request_id: request_id } },
    { immediate: false }
  );

  const checkFpjsData = () => {
    // Check if fingerprinting is disabled from props or if we already have dummy data
    if (internalFingerprintDisabled || fpjs?.visitorId === 'disabled-via-url-param') {
      // Make sure we have the dummy data set
      if (!fpjs) {
        setFpjs({ visitorId: 'disabled-via-url-param' });
      }
      
      // Make sure we marked fingerprint as sent
      if (!fingerprintSent) {
        setFingerprintSent(true);
      }
      
      return;
    }
    
    // Check if settings are available and fingerprinting is disabled in settings
    if (state.settings && state.settings.is_fingerprint_active === false) {
      setFpjs({ visitorId: 'disabled-via-settings' });
      setFingerprintSent(true);
      setInternalFingerprintDisabled(true);
      return;
    }
    
    // Check URL parameters as a backup
    const urlParams = new URLSearchParams(window.location.search);
    const fingerprintParam = urlParams.get('fingerprint');
    
    // If fingerprinting disabled via URL param, set dummy data immediately
    if (fingerprintParam === 'f') {
      setFpjs({ visitorId: 'disabled-via-url-param' });
      setFingerprintSent(true);
      setInternalFingerprintDisabled(true);
      return;
    }

    getData()
      .then((data) => {
        setFpjs(data);
        setOverload(false); // Reset overload if successful
      })
      .catch((error) => {
        if (error.message === FingerprintJSPro.ERROR_RATE_LIMIT) {
          setOverload(true);
        } else {
          setErrorGettingData(true);
        }
      });
  };

  useEffect(() => {
    if (overload) {
      const intervalId = setInterval(() => {
        checkFpjsData();
      }, 5000); // Retry every 3 seconds

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [overload]);

  // Initial fingerprint check and whenever settings change
  useEffect(() => {
    // Only run if fingerprinting is not already disabled
    if (!internalFingerprintDisabled && !fingerprintSent) {
      checkFpjsData();
    }
  }, [state.settings]); // Run when settings change

  // Fallback: if fingerprinting never completes, avoid blocking the flow
  useEffect(() => {
    if (internalFingerprintDisabled || fingerprintSent) return;

    const timeout = setTimeout(() => {
      console.warn('Fingerprinting timeout - proceeding without fingerprint data');
      setFingerprintSent(true);
      setFpjs({ visitorId: 'timeout-fallback' });
    }, 5000);

    return () => clearTimeout(timeout);
  }, [internalFingerprintDisabled, fingerprintSent]);

  useEffect(() => {
    // Check if fingerprinting is disabled or if we've already sent data
    if (internalFingerprintDisabled || fingerprintSent) {
      return;
    }
    
    // Check if we have the settings and fingerprint is active
    if (request_id && fpjs && state.settings) {
      // Skip if fingerprinting is disabled in settings
      if (state.settings && state.settings.is_fingerprint_active === false) {
        setFingerprintSent(true);
        return;
      }
      
      // Skip sending fingerprint data if it's our dummy data
      if (fpjs.visitorId === 'disabled-via-url-param') {
        setFingerprintSent(true);
        return;
      }
      
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Ensures the server knows you're sending JSON
        },
        body: JSON.stringify({
          request_id: request_id,
          fpjs_data: fpjs,
          site_name: state.settings?.site_name || null,
        }),
      };

      fetch("https://vx.dcams.app/frontend/session/fingerprint", requestOptions)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          setFingerprintSent(true);
          setIsABot(data.isBot);
          
          // Handle isBlocked if it exists and is true
          if (data.isBlocked === true) {
            setIsBlocked(true);
            // Send a message to parent window
            postMessage("ALREADY VERIFIED");
          }
        })
        .catch((error) => {
          //setErrorGettingData(true);
          console.error("Error storing fingerprint:", error);
        });
    }
  }, [request_id, fpjs, fingerprintSent, state.settings, internalFingerprintDisabled]);

  // Fetch initial data with edge retry logic
  const fetchAndCheck = async (url, request_id, isEdge = false) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ request_id }),
      });

      if (!response.ok) {
        console.warn(`Error with request to ${url}: ${response.statusText}`);
        return null;
      }

      const result = await response.json();
      
      // Check for edge "Not Found" error specifically
      if (isEdge && result.error === "Not Found" && 
          result.message === "No session data found for the given request ID.") {
        console.log('🔄 Edge session not found, will retry...');
        return { edgeNotFound: true };
      }

      if (!result.data || result.data === false) {
        console.warn(`No valid data returned from ${url}`);
        return null;
      }

      return result;
    } catch (error) {
      console.warn(`Failed to fetch from ${url}:`, error);
      return null;
    }
  };

  const handleRouteSequence = (dispatch, result, nextPath) => {
    if (result.data.settings && result.data.settings.journey.route) {
      let updatedRouteSequence = result.data.settings.journey.route.map(
        (route) => ({
          ...route,
          shouldProceed: (sessionState) =>
            sessionState[route.shouldProceedKey] === route.expectedStatus,
        })
      );

      if (nextPath) {
        const nextPathIndex = updatedRouteSequence.findIndex(
          (route) => route.path === nextPath
        );

        if (nextPathIndex > -1) {
          updatedRouteSequence = updatedRouteSequence.filter(
            (_, index) => index >= nextPathIndex
          );
        } else {
          const lastRoute =
            updatedRouteSequence[updatedRouteSequence.length - 1];
          const newRoute = {
            ...lastRoute,
            path: nextPath,
            shouldProceedKey: nextPath,
          };

          updatedRouteSequence.unshift(newRoute);
        }

        if (nextPath === "dcams/mitek") {
          updatedRouteSequence = [updatedRouteSequence[0]];
        }
      }

      dispatch({ type: "SET_ROUTE_SEQUENCE", payload: updatedRouteSequence });
    }
  };

  // Check for frontend query param
  const frontendParam = searchParams.get('frontend');
  
  // Determine if we should use VX backend based on hostname or query param
  const useVxBackend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";
  
  // Check if edge parameter is true OR if request_id ends with "-edge" (for OneID return flow)
  const stateParam = searchParams.get('state');
  const isEdgeFromRequestId = request_id?.endsWith('-edge') || stateParam?.endsWith('-edge');
  const useEdgeEndpoint = edge === "true" || isEdgeFromRequestId;
  
  const getPrimaryUrl = useEdgeEndpoint
    ? process.env.REACT_APP_EDGE_SESSION_URL
    : useVxBackend
    ? "https://vx.dcams.app/frontend/session/get_session"
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const getSecondaryUrl = useEdgeEndpoint
    ? process.env.REACT_APP_EDGE_SESSION_URL
    : !useVxBackend
    ? "https://vx.dcams.app/frontend/session/get_session"
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const fetchData = async (request_id, dispatch, nextPath) => {
    try {
      let result = await fetchAndCheck(getPrimaryUrl, request_id, useEdgeEndpoint);
      
      // Handle edge retry logic
      if (useEdgeEndpoint && result?.edgeNotFound && edgeRetryCount < 5) {
        console.log(`🔄 Edge retry ${edgeRetryCount + 1}/5`);
        setShowHighTrafficMessage(true);
        setEdgeRetryCount(prev => prev + 1);
        
        // Wait 2 seconds before retrying
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Retry the fetch
        return fetchData(request_id, dispatch, nextPath);
      }

      if (!result || result?.edgeNotFound) {
        console.warn(
          `Primary URL failed, trying secondary URL: ${getSecondaryUrl}`
        );
        result = await fetchAndCheck(getSecondaryUrl, request_id, false);
      }

      if (result) {
        const isReactNativeFlag =
          result.data?.isReactNative ??
          result.data?.settings?.isReactNative ??
          false;
        dispatch({
          type: "SET_DATA",
          payload: { ...result.data, isReactNative: isReactNativeFlag },
        });
        dispatch({
          type: "SET_ALREADY_DONE",
          payload: nextPath ? false : result.already_done,
        });

        // Settings received from the backend

        dispatch({ type: "SET_SETTINGS", payload: result.data.settings });
        dispatch({ type: "SET_TEXT", payload: result.data.settings.text });

        handleRouteSequence(dispatch, result, nextPath);
        setErrorGettingData(false);
        setRetryCount(0); // Reset retry count on successful fetch
      } else {
        // Both URLs failed
        console.error("Failed to fetch data from both primary and secondary URLs");
        setErrorGettingData(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setErrorGettingData(true);
    }
  };

  const handleRetry = () => {
    if (retryCount < 1) {
      setRetryCount((prevCount) => prevCount + 1);
      fetchData(request_id, dispatch, nextPath);
    }
  };

  // Removed initialSessionData handling - now fetching directly

  // Fetch session data if not available
useEffect(() => {
  // Skip if we already have data
  if (state.data) {
    // We already have the data from initialization, just handle nextPath adjustments if needed
    if (nextPath && state.routeSequence.length > 0) {
      let updatedRouteSequence = [...state.routeSequence];
      
      const nextPathIndex = updatedRouteSequence.findIndex(
        (route) => route.path === nextPath
      );

      if (nextPathIndex > -1) {
        updatedRouteSequence = updatedRouteSequence.filter(
          (_, index) => index >= nextPathIndex
        );
      } else {
        const lastRoute =
          updatedRouteSequence[updatedRouteSequence.length - 1];
        const newRoute = {
          ...lastRoute,
          path: nextPath,
          shouldProceedKey: nextPath,
        };

        updatedRouteSequence.unshift(newRoute);
      }

      if (nextPath === "dcams/mitek") {
        updatedRouteSequence = [updatedRouteSequence[0]];
      }
      
      dispatch({ type: "SET_ROUTE_SEQUENCE", payload: updatedRouteSequence });
    }

    setErrorGettingData(false);
    return; // Don't continue with the fetch if we already have data
  }

  // Fetch session data
  const doFetch = async () => {
    let localRequestId = request_id

    // Case A: if request_id is clear / oneid / digilocker, set localRequestId to the 'state' query param
    if (
      localRequestId === 'clear' ||
      localRequestId === 'clear/return' ||
      localRequestId === 'oneid' ||
      localRequestId === 'oneid/return' ||
      localRequestId === 'digilocker' ||
      localRequestId === 'digilocker/return/success' ||
      localRequestId === 'digilocker/return/failure'
    ) {
      localRequestId = searchParams.get('state')
      // Remove "-edge" suffix if present for the actual request_id
      if (localRequestId?.endsWith('-edge')) {
        localRequestId = localRequestId.replace('-edge', '')
      }
    } else if (localRequestId?.endsWith('-edge')) {
      // If request_id itself ends with "-edge", remove it
      localRequestId = localRequestId.replace('-edge', '')
    }
    // Case B: if request_id is "mdl", fetch the real request_id using the session_id query param
    else if (localRequestId === 'mdl') {
      const session_id = searchParams.get('sessionId')
      if (!session_id) {
        console.error('No session_id in query params.')
        return
      }
      try {
        localRequestId = await getRequestIdFromSession(session_id)
      } catch (err) {
        console.error('Could not exchange session for token:', err)
        setErrorGettingData(true)
        return
      }
    }

    // Case C: if we have a valid localRequestId and it's not "upload", fetch the data
    if (localRequestId && localRequestId !== 'upload') {
      try {
        await fetchData(localRequestId, dispatch, nextPath)
        setErrorGettingData(false)
      } catch {
        setErrorGettingData(true)
      }
    }
  }

  // Only fetch if we don't have data yet
  if (!state.data) {
    doFetch()
  }
}, [request_id, dispatch, nextPath, state.data])

  useEffect(() => {
    if (qr && state.settings) {
      const requestOptions = {
        method: "POST",
        body: JSON.stringify({
          request_id: state.settings.request_id,
          type: "set",
          site_name: state.settings.site_name,
        }),
      };

      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          //console.log("QR status update successful:", data);
        })
        .catch((error) => {
          console.error("Error updating QR status:", error);
        });
    } else {
      if (qr) {
        //console.log("QR Code detected but settings are not available yet.");
      }
    }
  }, [qr, state.settings]);

  const navigateToNext = () => {
    // Check if it's the right time to navigate to the next route
    if (state.isCurrentComponentComplete && state.status === "PENDING") {
      dispatch({ type: "INCREMENT_COMPLETED_ROUTES" });
      const currentRoute = state.routeSequence[state.currentIndex];
      const nextIndex = state.currentIndex + 1;
      const nextRoute = state.routeSequence[nextIndex];
      if (state.settings.journey.settings?.isModular) {
        navigate("agematch/modular");
      }
      if (nextRoute) {
        dispatch({ type: "SET_CURRENT_INDEX", payload: nextIndex });
        navigate(nextRoute.path);
      } else {
        //window.top.postMessage("FAIL", "*");
        // Handle the end of the sequence or define a default action if required
      }
    }
  };

  useEffect(() => {
    let event = null;
    let next = state.triggerNavigation ? true : false;
    let error = "NONE";

    if (next) {
      event = "NEXT";
    } else if (state.closedClicked) {
      event = "CLOSED";
    } else if (state.alreadyDone) {
      event = "ERROR";
      error = "ALREADY_DONE";
    } else {
      switch (state.status) {
        case "PASS":
        case "FAIL":
          event = "COMPLETE";
          break;
        default:
          event = null;
      }
    }

    const payload = {
      event: event,
      app: state.routeSequence[state.currentIndex]?.app,
      error: error,
    };
    if (state?.settings?.site_name === "veratad") {
      console.log("post message sent", payload);
      postMessage({ messageType: "payload", value: payload });
    }

    if (state.status === "PASS" || state.status === "FAIL") {
      postMessage(state.status);
    }
  }, [state.status, state.triggerNavigation, state.closedClicked]);

  useEffect(() => {
    //console.log("route sequence", state.routeSequence);
    if (!state.triggerNavigation) {
      return;
    }
    navigateToNext();
  }, [state.triggerNavigation, state.routeSequence]); // Only re-run if these specific parts of the state change

  // Context value that will be provided to the consuming components
  const contextValue = {
    ...state, // Spread the state
    dispatch, // Allow dispatch to be used by consuming components
    navigateToNext, // Add other functions and values here
    postMessage,
  };


  //const inIframe = window.self !== window.top;
  
  // Only render children when:
  // 1. We have data from the backend
  // 2. User is not a bot or blocked
  // 3. We have fingerprint data (or fingerprinting is disabled)
  // 4. We have completed the fingerprint check (fingerprintSent is true)
  // 5. Loading animation has completed (if English loading was shown)
  const dataReady =
    state.data &&
    !isBot &&
    !isBlocked &&
    (fpjs || internalFingerprintDisabled) &&
    fingerprintSent;
    
  const shouldRenderChildren = dataReady && loadingAnimationComplete;

  // Handle loading animation completion
  const handleLoadingComplete = () => {
    console.log('✅ Loading animation completed');
    setLoadingAnimationComplete(true);
  };

  // Delay showing English loading to prevent restart when data arrives quickly
  // Once we start showing it, keep showing it until loading is complete
  useEffect(() => {
    if (shouldShowEnglishLoading && !showEnglishLoadingDelayed && !loadingAnimationComplete) {
      const timer = setTimeout(() => {
        console.log('⏰ Delayed English loading activated');
        setShowEnglishLoadingDelayed(true);
      }, 500); // 500ms delay to prevent flicker
      
      return () => clearTimeout(timer);
    }
  }, [shouldShowEnglishLoading, showEnglishLoadingDelayed, loadingAnimationComplete]);
  
  // Debug logging for loading state
  useEffect(() => {
    console.log('🔍 Loading state:', {
      shouldShowEnglishLoading,
      showEnglishLoadingDelayed,
      loadingAnimationComplete,
      hasData: !!state.data,
      status: state.status,
      instanceId: instanceId.current
    });
  }, [showEnglishLoadingDelayed, loadingAnimationComplete, state.data, state.status]);

  // Determine what content to render (but don't return yet)
  let content;
  let contentType = 'unknown';
  
  // Check for error states first
  if (state.alreadyDone || errorGettingData) {
    contentType = 'error-expired';
    content = (
      <ErrorMessage
        title="Link Expired"
        subtitle={
          <>
            <Text>
              This link is either invalid, expired, or has already been used
            </Text>
            {retryCount <= 0 && (
              <Button
                mt="lg"
                fullWidth
                onClick={handleRetry}
                disabled={retryCount >= 1}
              >
                Try Again
              </Button>
            )}
          </>
        }
      />
    );
  } else if (overload) {
    contentType = 'overload';
    content = (
      <ErrorMessage
        title="Please Wait"
        subtitle={
          <Group>
            <Loader size={"xs"} />
            <Text>We are getting ready for your verification</Text>
          </Group>
        }
      />
    );
  } else if (isBot || isBlocked) {
    contentType = 'bot-blocked';
    content = (
      <ErrorMessage
        title="You May Not Continue"
        subtitle={<Text>You are unable to continue this process</Text>}
      />
    );
  } else if (state.status !== "PENDING") {
    contentType = 'status-result';
    // Handle PASS/FAIL status
    const messages = state.data?.resultMessages ?? {};
    const successTitle =
      messages.success?.verificationSuccessTitle ?? "Verification Success";
    const successSubTitle =
      messages.success?.verificationSuccessSubTitle ??
      "Please return to the other tab";
    const failureTitle =
      messages.failure?.verificationFailureTitle ?? "Verification Failure";
    const failureSubTitle =
      messages.failure?.verificationFailureSubTitle ??
      "Please return to the other tab";

    // Create theme configuration for the overlay
    const colors_array = state.data?.styles?.colors
      ? state.data.styles.colors
      : {
          "vx-green": [
            "#eff6fb",
            "#dee9f1",
            "#b8d2e5",
            "#8fbad8",
            "#6ea5ce",
            "#5998c8",
            "#4d92c6",
            "#3e7eaf",
            "#34709d",
            "#22618b",
          ],
        };

    const themeConfig = {
      colors: colors_array,
      colorScheme: state.data?.styles?.colorScheme
        ? state.data.styles.colorScheme
        : "light",
      primaryColor: state.data?.styles?.primaryColor
        ? state.data.styles.primaryColor
        : "vx-green",
      primaryShade: state.data?.styles?.primaryShade ? state.data.styles.primaryShade : 5,
      components: {
        Button: {
          defaultProps: {
            radius: state.data?.styles?.button?.radius
              ? state.data.styles.button.radius
              : 6,
          },
        },
      },
      defaultRadius: state.data?.styles?.defaultRadius ? state.data.defaultRadius : 6,
    };

    content = (
      <MantineProvider theme={themeConfig} withGlobalStyles withNormalizeCSS>
        <Navigation logo_url={""} />
        <CenterLoadingOverlay
          content={
            <Center>
              <Stack align="center">
                <ThemeIcon
                  variant="light"
                  color={state.status === "PASS" ? "green" : "red"}
                  size={95}
                  radius="xl"
                >
                  {state.status === "PASS" ? (
                    <IconShieldCheck size={85} stroke={1.5} />
                  ) : (
                    <IconExclamationCircle size={85} stroke={1.5} />
                  )}
                </ThemeIcon>
                <Text align="center" fz="xl" fw={700}>
                  {state.status === "PASS" ? successTitle : failureTitle}
                </Text>
                <Text align="center" mt="md" fz="md">
                  {state.status === "PASS" ? successSubTitle : failureSubTitle}
                </Text>
              </Stack>
            </Center>
          }
        />
      </MantineProvider>
    );
  } else if (shouldRenderChildren) {
    contentType = 'children';
    // Everything is ready, render children
    content = children;
  } else {
    // Still loading - show appropriate loading component
    // Once English loading has started showing, keep showing it until complete
    if (showEnglishLoadingDelayed && !loadingAnimationComplete) {
      // Show English loading (once started, always show until complete)
      contentType = 'english-loading';
      // Use high traffic messages if we're in edge retry mode
      const loadingMessages = showHighTrafficMessage ? [
        "Experiencing high traffic...",
        "Please wait while we connect...",
        "Processing your request...",
        "Almost there...",
        "Finalizing connection..."
      ] : [
        "Getting your session ready...",
        "Next, you’ll confirm your age and identity...",
        "Your data is encrypted and never shared...",
        "You’re about to join millions of other verified users...",
        "Finalizing..."
      ];
      
      content = (
        <EnglishLoadingWithCompletion 
          key="english-loading" // Key ensures it doesn't remount
          onComplete={handleLoadingComplete}
          messages={loadingMessages}
        />
      );
    } else if (shouldShowEnglishLoading && !showEnglishLoadingDelayed) {
      // Show simple loading overlay initially while waiting for delay
      contentType = 'simple-loading-pre-english';
      content = <LoadingOverlay visible />;
    } else {
      // Show simple loading overlay for non-English browsers
      contentType = 'simple-loading';
      content = <LoadingOverlay visible />;
    }
  }

  // Log what we're about to render
  console.log('🎯 DataContext render decision:', {
    contentType,
    loadingAnimationComplete,
    shouldShowEnglishLoading,
    shouldRenderChildren,
    dataReady,
    hasData: !!state.data,
    status: state.status,
    timestamp: new Date().toISOString()
  });

  // Single return at the end after all hooks
  return (
    <DataContext.Provider value={contextValue}>
      {content}
    </DataContext.Provider>
  );
};
